import type * as React from 'react'
import { useCallback } from 'react'
import type { JSX } from 'react'

import { Dialog, Text, toast } from '@buffer-mono/popcorn'

import { TemplateDetails } from '~publish/components/TemplateDetails'
import type { TemplateDetailsData } from '~publish/components/TemplateDetails'

import styles from './TemplatePreviewDialog.module.css'

export function useCopyTemplateBody(body: string | undefined): () => void {
  return useCallback((): void => {
    if (body === undefined) return
    navigator.clipboard
      .writeText(body)
      .then(() => {
        return toast.success('Template copied to clipboard')
      })
      .catch(() => {
        return toast.error('Failed to copy template to clipboard')
      })
  }, [body])
}

type TemplatePreviewDialogProps = {
  template: TemplateDetailsData | undefined
  open?: boolean
  onOpenChange: (open: boolean) => void
  actions: React.ReactNode
  metadata?: React.ReactNode
  headerAction?: React.ReactNode
  trigger?: React.ReactNode
}

export const TemplatePreviewDialog = ({
  template,
  open,
  onOpenChange,
  actions,
  metadata = <TemplateDetails.Metadata />,
  headerAction,
  trigger,
}: TemplatePreviewDialogProps): JSX.Element => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Trigger>{trigger}</Dialog.Trigger>
      <Dialog.Content className={styles.content}>
        {template ? (
          <TemplateDetails template={template} className={styles.details}>
            <Dialog.Header className={styles.section}>
              <Dialog.Title size="large" className={styles.title}>
                <TemplateDetails.IconSwatch>
                  <TemplateDetails.Icon />
                </TemplateDetails.IconSwatch>
                {template.title}
              </Dialog.Title>
              <Dialog.Description>
                <Text size="lg" color="subtle">
                  {template.description}
                </Text>
              </Dialog.Description>
            </Dialog.Header>
            <Dialog.Body className={styles.section}>
              <TemplateDetails.Body>
                <TemplateDetails.Actions>{actions}</TemplateDetails.Actions>
              </TemplateDetails.Body>
              {metadata}
            </Dialog.Body>
          </TemplateDetails>
        ) : null}
        {headerAction}
        <Dialog.CloseButton />
      </Dialog.Content>
    </Dialog>
  )
}
